import React, { useEffect, useState, useRef} from "react";
import "./Modals.scss";
import AuiFileUpload from "../AuiDropdown/AuiFIleUpload";
export type modal = "auimodal-lg" | "auimodal-md" | "auimodal-sm";
export type align = "left" | "center" | "right";

export interface IconProps {
  icon: string;
  svgclass?: string;
  svgwidth?: string;
  svgheight?: string;
  pathclass?: string;
  ariaLabel?:string;
}

export interface Modalprops {
  modalsize: "auimodal-lg" | "auimodal-md" | "auimodal-sm";
  className?: any;
  id?: string;
  children?: any;
  modalTitle?: string;
  modalButtonTitle?: string;
  modalButtonCancel?: string;
  headingAlign?: align;
  footerAlign?: align;
  buttonTitle?: string;
  modalid?: any;
  note?: string;
  variant:
    | "black-primary"
    | "button-text-styled"
    | "general-text-link"
    | "link-style"
    | "link-style-arrow"
    | "primary"
    | "read-more"
    | "secondary";
  action?: Function;
  onClick?: Function;
  iconProps?: IconProps;
  modalType?: "normal-modal" | "input-modal" | "upload-modal";
  disableActionButton?: boolean;
  resetModal?: () => void;
  customIcon?: React.ReactNode;
  auiFileUploadProps?: React.ComponentProps<typeof AuiFileUpload>;
  uploadedFiles? : FileList | null | string;
  setUploadedFiles?: React.Dispatch<React.SetStateAction<FileList | null | string>>
  onModalOpen?: () => void
}

const Modals = ({
  id,
  modalsize,
  children,
  modalTitle,
  modalButtonTitle,
  modalButtonCancel = "Cancel",
  headingAlign = "left",
  footerAlign = "right",
  buttonTitle,
  modalid,
  variant,
  action,
  iconProps,
  modalType = "normal-modal",
  disableActionButton = false,
  resetModal,
  customIcon,
  auiFileUploadProps,
  uploadedFiles,
  setUploadedFiles,
  onModalOpen,
}: Modalprops) => {
  const [note, setNote] = useState("");
  const [disableAction, setDisableAction] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const textareaElement = document.querySelector(`aui-textarea#${id}`);
    if (!textareaElement) return;
    setTimeout(() => {
      const shadowInput = textareaElement.shadowRoot?.querySelector(
        "textarea, input"
      ) as HTMLTextAreaElement | HTMLInputElement | null;
      if (!shadowInput) return;
      const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === " " && shadowInput.value.length === 0) {
          event.preventDefault();
        }
      };
      const getValue = async (event: Event) => {
        const customEvent = event as CustomEvent<string>;
        const newValue = customEvent.detail;

        setNote(newValue);
        setDisableAction(false);
      };
      shadowInput.addEventListener("keydown", handleKeydown as EventListener);
      textareaElement.addEventListener(
        "auiTextareaChange",
        getValue as EventListener
      );
      return () => {
        shadowInput.addEventListener("keydown", handleKeydown as EventListener);
        textareaElement.removeEventListener(
          "auiTextareaChange",
          getValue as EventListener
        );
      };
    }, 100);
  }, [note]);


  const isActionButtonDisabled =
  (modalType === "input-modal" && (!note || note.length < 10 || note.length > 255 || disableAction)) ||
  (modalType === "upload-modal" && (!uploadedFiles || uploadedFiles.length === 0));

  useEffect(() => {
    const modalElement = document.getElementById(modalid);
    const isApproveModal = modalid?.toLowerCase().includes("approve");

    if (!modalElement || !isApproveModal) return;

    lastFocusedElement.current = document.activeElement as HTMLElement;

    const focusAndTrap = () => {
      const isVisible = (el: HTMLElement): boolean => {
        return el.offsetParent !== null;
      };

      const getVisibleFocusableEls = (): HTMLElement[] => {
        const focusableEls = modalElement.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        return Array.from(focusableEls).filter(isVisible);
      };

      const visibleEls = getVisibleFocusableEls();
      if (visibleEls.length > 0) {
        visibleEls[0].focus();
      }

      const trapFocus = (e: KeyboardEvent) => {
        if (e.key !== "Tab") return;

        const visibleEls = getVisibleFocusableEls();
        if (visibleEls.length === 0) return;

        const firstEl = visibleEls[0];
        const lastEl = visibleEls[visibleEls.length - 1];

        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      };

      const preventBackdropClick = (e: MouseEvent) => {
        if (e.target === modalElement) {
          e.stopPropagation();
          e.preventDefault();
        }
      };

      modalElement.addEventListener("keydown", trapFocus);
      modalElement.addEventListener("click", preventBackdropClick);

      cleanupFns.push(() => {
        modalElement.removeEventListener("keydown", trapFocus);
        modalElement.removeEventListener("click", preventBackdropClick);
      });
    };

    const cleanupFns: (() => void)[] = [];
    const delay = setTimeout(focusAndTrap, 100); // Allow animation/render time

    return () => {
      clearTimeout(delay);
      cleanupFns.forEach((fn) => fn());
      lastFocusedElement.current?.focus();
    };
  }, [modalid]);

  return (
    <React.Fragment>
      <aui-button
        data-backdrop="static"
        data-keyboard="false"
        buttontitle={modalButtonTitle}
        arialabel={modalButtonTitle}
        id="launchModalButton"
        buttonclass="aui-modal-launch primary modalGenericClass"
        ifmodal={true}
        modalid={modalid}
        variant={variant}
        size="medium"
        onClick={() => {
          resetModal?.();
          onModalOpen?.();
          const modalElement = document.getElementById(modalid);
          if (modalElement) {
            modalElement.focus();
          }
        }}
        disabled={disableActionButton}
        data-testid="launch-modal-button"
      >
        {iconProps && (
          <aui-icon
            icon={iconProps?.icon}
            svgclass={iconProps?.svgclass}
            svgwidth={iconProps?.svgwidth}
            svgheight={iconProps?.svgheight}
            pathclass={iconProps?.pathclass}
            aria-label={iconProps?.ariaLabel}
          ></aui-icon>
        )}
        {customIcon && <>{customIcon}</>}
      </aui-button>

      <aui-modal modalsize={modalsize}>
        <div
          className="modal fade auimodal"
          id={modalid}
          aria-label={modalTitle}
          aria-hidden="true"
          data-testid="modal-sizes"
          aria-modal="true"
          aria-labelledby={`${modalid}-title`}
          tabIndex={-1}
          ref={modalRef}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content d-block">
              <div>
                {modalTitle && (
                  <div
                    className={`modal-header pb-2 pl-0 pt-0${
                      headingAlign ? " " + headingAlign : ""
                    }`}
                  >
                    <aui-modal-headtag>
                      <div id={`${modalid}-title`}>{modalTitle}</div>
                    </aui-modal-headtag>
                  </div>
                )}
              </div>
              <aui-modal-content>
                {modalType === "normal-modal" && (
                  <div className={`modal-body d-block ${modalType}`}>
                    {children}
                  </div>
                )}
                {modalType === "input-modal" && (
                  <div className={`modal-body d-block ${modalType}`}>
                    <aui-textarea
                      id={id}
                      textareaid="primary-textarea"
                      label="Add note"
                      labelgrid="col-sm-3"
                      textareagrid="col-sm-9"
                      minlength={10}
                      maxlength={255}
                      enableautofocus={false}
                      direction="column"
                      required={true}
                      placeholder=""
                      value={note}
                      errormessage="This field is required."
                      minlengthmessage="Please enter a minimum of 10 characters"
                      maxlengthmessage="Maximum characters allowed is 255"
                    ></aui-textarea>
                  </div>
                )}
                {modalType === "upload-modal" && auiFileUploadProps && (
                  <div className="modal-body d-block">
                    <AuiFileUpload {...auiFileUploadProps} />
                  </div>
                )}
                <div
                  className={`${
                    footerAlign ? footerAlign + " " : ""
                  }modal-footer auimodals-buttons d-flex w-100 justify-content-end align-items-center p-0 mt-3`}
                >
                  <aui-button
                    variant="read-more"
                    buttontitle={modalButtonCancel}
                    data-dismiss="modal"
                    size="medium"
                    buttonclass="modalSecondaryBtn aui-modal-button-width aui-modal-btn-trap aui-uploaddocument-btn-close aui-modalclosecls font-500 cancelBtn"
                    onClick={(e: any) => {
                      e.preventDefault();
                      resetModal?.();
                      setNote("");
                      setUploadedFiles?.(null);
                      lastFocusedElement.current?.focus();
                    }}
                    data-testid="cancel-modal-button"
                    arialabel={modalButtonCancel}
                  ></aui-button>
                   <aui-button
                    variant="primary"
                    buttontitle={buttonTitle}
                    disabled={isActionButtonDisabled || disableActionButton}
                    data-dismiss="modal"
                    buttonid={""}
                    size="medium"
                    buttonclass="aui-modal-button-width aui-modal-btn-trap aui-uploaddocument-btn-close aui-modalclosecls font-400"
                    data-testid="submit-modal-button"
                    onClick={(e: any) => {
                      e.preventDefault();
                      if (isActionButtonDisabled || disableActionButton) return;
                      if(!disableActionButton) {
                        action?.();
                        setNote("");
                        resetModal?.();
                      }
                    }}
                  ></aui-button>
                </div>
              </aui-modal-content>
            </div>
          </div>
        </div>
      </aui-modal>
    </React.Fragment>
  );
};

export default Modals;
